import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  AirtimeProviderState,
  AirtimeProviderResponse,
  AirtimeProvider,
} from "./types/airtime-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: AirtimeProviderState = {
  airtimeProviders: [],
  allAirtimeProviders: [],
  airtimeProvider: null,
  selectedAirtimeProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE Provider
export const createAirtimeProvider = createAsyncThunk<
  AirtimeProviderResponse,
  FormData | Omit<AirtimeProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/create", async (airtimeProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = airtimeProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/airtime-providers`,
      airtimeProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      airtimeProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create airtime provider" },
    );
  }
});

// READ (fetch all) AirtimeProviders
export const fetchAirtimeProviders = createAsyncThunk<
  AirtimeProviderResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      sort_by,
      sort_order,
      category,
      country,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      // Build search/filter query parameters only for the base URL request
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/airtime-providers`, {
        params,
        headers,
      });
    }
    return {
      message: res.data.message,
      airtimeProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch airtime providers" },
    );
  }
});

// READ (fetch all providers without pagination)
export const fetchAllAirtimeProviders = createAsyncThunk<
  AirtimeProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/airtime-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch airtime providers" },
    );
  }
});

// READ (fetch one) DataProvider by ID
export const fetchAirtimeProviderById = createAsyncThunk<
  AirtimeProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/airtime-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch airtime provider" },
    );
  }
});

// UPDATE DataProvider
export const updateAirtimeProvider = createAsyncThunk<
  AirtimeProvider,
  { id: number; data: FormData | Partial<AirtimeProvider> },
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/airtime-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data provider" },
    );
  }
});

// DELETE DataProvider
export const deleteAirtimeProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimeProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/airtime-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data provider" },
    );
  }
});

// Slice
const airtimeProviderSlice = createSlice({
  name: "airtimeProviders",
  initialState,
  reducers: {
    resetAirtimeProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedAirtimeProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createAirtimeProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAirtimeProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.airtimeProvider) {
          state.airtimeProviders.unshift(action.payload.airtimeProvider);
        }
      })
      .addCase(createAirtimeProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create data provider";
      })

      // Fetch All
      .addCase(fetchAirtimeProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAirtimeProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.airtimeProviders = action.payload.airtimeProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchAirtimeProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data providers";
      })
      .addCase(fetchAllAirtimeProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllAirtimeProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allAirtimeProviders = action.payload || [];
      })
      .addCase(fetchAllAirtimeProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch airtime providers";
      })

      // Fetch One
      .addCase(fetchAirtimeProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedAirtimeProvider = null;
      })
      .addCase(fetchAirtimeProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAirtimeProvider = action.payload;
      })
      .addCase(fetchAirtimeProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data provider";
      })

      // Update
      .addCase(updateAirtimeProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAirtimeProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.airtimeProviders = state.airtimeProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedAirtimeProvider?.id === action.payload.id) {
          state.selectedAirtimeProvider = action.payload;
        }
      })
      .addCase(updateAirtimeProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update data provider";
      })

      // Delete
      .addCase(deleteAirtimeProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAirtimeProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.airtimeProviders = state.airtimeProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteAirtimeProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete data provider";
      });
  },
});

export const { resetAirtimeProviderState } = airtimeProviderSlice.actions;
export default airtimeProviderSlice.reducer;
